<template>
  <b-card class="rounded-lg">
    <report-header
      :title="$t('ledger_rep_employee')"
      :scope-label="scopeLabel"
      :can-company="canCompany"
      :company="company"
      @company="company = $event; load()"
      @print="printReport"
    />
    <b-row class="mb-1">
      <b-col cols="12" md="5">
        <b-form-input v-model="query" :placeholder="$t('ledger_search')" @keyup.enter="searchUsers" />
      </b-col>
      <b-col cols="6" md="2">
        <b-form-input v-model.number="year" type="number" />
      </b-col>
      <b-col cols="6" md="2">
        <b-form-input v-model.number="month" type="number" min="1" max="12" />
      </b-col>
      <b-col cols="12" md="2">
        <b-button variant="primary" size="sm" @click="load">{{ $t('ledger_assets_load') }}</b-button>
      </b-col>
    </b-row>
    <div v-if="users.length" class="mb-1">
      <b-button
        v-for="user in users"
        :key="user.id"
        size="sm"
        class="mr-50 mb-50"
        :variant="Number($route.params.userId) === user.id ? 'primary' : 'outline-primary'"
        @click="pick(user)"
      >
        {{ user.name }}
      </b-button>
    </div>
    <b-alert v-if="wrongBranch" show variant="warning">{{ $t('ledger_rep_wrong_branch') }}</b-alert>
    <div v-if="loading" class="text-center py-3"><b-spinner /></div>
    <template v-else-if="report">
      <h5>{{ report.user && report.user.name }}</h5>
      <h6 v-if="report.custody" class="mt-1">عهدة الموظف</h6>
      <div v-if="report.custody" class="table-responsive mb-1">
        <table class="table table-sm mb-0">
          <thead>
            <tr>
              <th>المحفظة</th>
              <th>الحالة</th>
              <th>الرصيد الحالي</th>
              <th>رصيد أول المدة</th>
              <th>رصيد آخر المدة</th>
              <th>داخل</th>
              <th>خارج</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="wallet in report.custody.wallets || []"
              :key="wallet.wallet_id"
            >
              <td>{{ wallet.wallet_id }}</td>
              <td>{{ custodyStatus(wallet) }}</td>
              <td
                class="ledger-num"
                :class="{ 'text-danger': isNegative(wallet.current_balance) }"
              >
                {{ money(wallet.current_balance) }}
              </td>
              <td class="ledger-num">{{ money(wallet.opening_balance) }}</td>
              <td class="ledger-num">{{ money(wallet.closing_balance) }}</td>
              <td class="ledger-num">{{ money(wallet.total_in) }}</td>
              <td class="ledger-num">{{ money(wallet.total_out) }}</td>
            </tr>
            <tr v-if="report.custody.totals" class="font-weight-bold">
              <td colspan="2">الإجمالي</td>
              <td
                class="ledger-num"
                :class="{ 'text-danger': isNegative(report.custody.totals.current_balance) }"
              >
                {{ money(report.custody.totals.current_balance) }}
              </td>
              <td />
              <td />
              <td class="ledger-num">{{ money(report.custody.totals.total_in) }}</td>
              <td class="ledger-num">{{ money(report.custody.totals.total_out) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <h6 class="mt-1">{{ $t('ledger_rep_transactions') }}</h6>
      <div v-for="item in (report.transactions && report.transactions.items) || []" :key="item.id">
        {{ item.transaction_number }} <span class="ledger-num">{{ money(item.amount) }}</span>
      </div>
      <h6 class="mt-1">{{ $t('ledger_payslip_title') }}</h6>
      <div v-for="slip in report.payslips || []" :key="slip.payroll_run_id" class="ledger-num">
        {{ slip.year }}/{{ slip.month }} {{ money(slip.net) }}
      </div>
    </template>
  </b-card>
</template>

<script>
import { BCard, BRow, BCol, BFormInput, BButton, BAlert, BSpinner } from 'bootstrap-vue'
import User from '@/api/user_settings/users/user'
import { getEmployeeReport } from '@/api/accounting/financialReports'
import { listFrom, oneFrom } from '@/utils/ledger'
import ReportHeader from './ReportHeader.vue'
import reportMixin from './reportMixin'

const usersApi = new User()

export default {
  name: 'EmployeeReport',
  components: { BCard, BRow, BCol, BFormInput, BButton, BAlert, BSpinner, ReportHeader },
  mixins: [reportMixin],
  data() {
    return { query: '', users: [], wrongBranch: false }
  },
  watch: {
    '$route.params.userId': 'load',
  },
  mounted() {
    if (this.$route.params.userId) this.load()
  },
  methods: {
    custodyStatus(wallet) {
      const status = wallet && wallet.status
      if (!status) return ''
      if (typeof status === 'string') return status
      return status.label_ar || status.label || status.value || ''
    },
    isNegative(value) {
      const amount = Number(value)
      return Number.isFinite(amount) && amount < 0
    },
    pick(user) {
      this.$router.push({ name: 'ledger-report-employee', params: { userId: user.id } })
    },
    async searchUsers() {
      try {
        const res = await usersApi.list({ search: this.query, per_page: 15, page: 1 })
        this.users = listFrom(res)
      } catch (error) {
        this.users = []
      }
    },
    async load() {
      const userId = this.$route.params.userId
      if (!userId) return
      this.loading = true
      this.wrongBranch = false
      try {
        const res = await getEmployeeReport(userId, { year: this.year, month: this.month, ...this.scopeParams })
        this.report = oneFrom(res)
      } catch (error) {
        this.report = null
        if (error?.response?.status === 403) this.wrongBranch = true
        else this.toastError(error)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-num { font-variant-numeric: tabular-nums; }
</style>
